// Library Imports
import * as React from "react";
import {
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  capitalize,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useHistory } from "react-router-dom";
import { ImagesContext } from "../../components/Context";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

// header import
import { getHeaders } from "../utils/HeaderHelper";

// app setting imports
import { AuthenticationContext } from "../Context";
import ClaimHistory from "../billing/ClaimHistory";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";
import ClaimTypeSelection from "../billing/ClaimTypeSelection";
import CreateFirstTemplateModal from "../billing/CreateFirstTemplateModal";
import ChooseClaimTemplateModal from "../billing/ChooseClaimTemplateModal";
import { formatMonetaryValue } from "../utils/MonetaryFormatHelper";
import { useFlash } from "../FlashContext";

interface Claim {
  id: string;
  created_at: string;
  payor: string;
  claim_number: string;
  total_amount: number;
  submitted_at: string;
  claim_status: string;
  charge_lines: any[];
  claim_type?: string;
}

interface Props {
  patientId: string;
  encounterId: string;
  menu_track_src: string;
  placeOfServiceList?: any;
}

const EncounterClaimInfoPanel: React.FC<Props> = (props) => {
  const history = useHistory();
  const { showToast } = useFlash();
  const authenticationSetting = React.useContext(AuthenticationContext);
  const [panelExpanded, setPanelExpanded] = React.useState<boolean>(true);
  const [claims, setClaims] = React.useState<Claim[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showClaimHistory, setShowClaimHistory] =
    React.useState<boolean>(false);
  const images = React.useContext(ImagesContext);
  const [selectedClaimId, setSelectedClaimId] = React.useState("");
  const [showClaimTypeSelectionModal, setShowClaimTypeSelectionModal] =
    React.useState<boolean>(false);
  const [showCreateFirstTemplate, setShowCreateFirstTemplate] =
    React.useState(false);
  const [showChooseClaimTemplate, setShowChooseClaimTemplate] =
    React.useState(false);
  const [claimTemplates, setClaimTemplates] = React.useState<any[]>([]);

  const fetchClaimTemplates = () => {
    fetch(`/claim_templates/published`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setClaimTemplates(result.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  React.useEffect(() => {
    fetchClaimTemplates();
  }, [props.encounterId]);

  React.useEffect(() => {
    getClaims();
  }, [props.encounterId]);

  const getClaims = () => {
    if (props.encounterId) {
      setLoading(true);
      fetch(`/billing/claims?encounter_uuid=${props.encounterId}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success) {
            setClaims(result.resource || []);
          } else {
            showToast({ title: "Error", text: result.message || "Failed to load claims.", type: "error" });
          }
          setLoading(false);
        })
        .catch((error) => {
          showToast({ title: "Error", text: "Something went wrong fetching claims.", type: "error" });
          setLoading(false);
        });
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount: number) => {
    if (!amount) return "$0.00";
    const formatted = formatMonetaryValue(amount);
    return formatted ? `$${formatted}` : "$0.00";
  };

  const renderStatus = (status: string) => {
    let tagContainer = "label-tag-container";
    let tagLabel = "label-tag";

    switch (status?.toLowerCase()) {
      case "draft":
        tagContainer = "label-tag-container tertiary-container";
        tagLabel = "label-tag tertiary-tag";
        break;
      case "pending submission":
        tagContainer = "label-tag-container secondary-container";
        tagLabel = "label-tag secondary-tag";
        break;
      case "submitted":
        tagContainer = "label-tag-container primary-container";
        tagLabel = "label-tag primary-tag";
        break;
    }

    return (
      <div className={tagContainer} style={{ marginLeft: 0, marginBottom: 10 }}>
        <p className={tagLabel}>{snakeCaseToTitleCase(status)}</p>
      </div>
    );
  };

  const handleDeleteClaim = (claimId: string) => {
    if (window.confirm("Are you sure you want to delete this claim?")) {
      fetch(`/billing/claims/${claimId}`, {
        method: "DELETE",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success) {
            showToast({ title: "Success", text: "Claim deleted successfully.", type: "success" });
            getClaims(); // Refresh the claims list
          } else {
            showToast({ title: "Error", text: result.message || "Failed to delete claim.", type: "error" });
          }
        })
        .catch((error) => {
          showToast({ title: "Error", text: "Something went wrong deleting the claim.", type: "error" });
        });
    }
  };

  const handleShowClaimHistory = (claimId) => {
    setSelectedClaimId(claimId);
    openShowClaimHistoryModal();
  };

  const openShowClaimHistoryModal = () => {
    setShowClaimHistory(true);
  };

  const closeShowClaimHistoryModal = () => {
    setSelectedClaimId("");
    setShowClaimHistory(false);
  };

  const openShowClaimSelectionModal = () => {
    setShowClaimTypeSelectionModal(true);
  };

  return (
    <Grid container className="panel-container" borderRadius={"4px"}>
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  paddingX={3}
                  paddingY={1}
                  alignItems={"center"}
                  display={"flex"}
                >
                  <Grid item>
                    <p className="panel-header">Claim Information</p>
                  </Grid>
                  <div className="row">
                    <Link
                      className="action-link row"
                      onClick={openShowClaimSelectionModal}
                    >
                      <img
                        src={images.add_icon}
                        alt="Add Insurance"
                        className="action-icon-image"
                        style={{ width: 31.466, maxHeight: 32 }}
                      />
                      <span className="app-user-text disable-pointer">
                        Add New Claim
                      </span>
                    </Link>
                  </div>
                </Stack>
              </Grid>
            </Grid>
            <Grid container className="general-patient-info-container">
              <Grid
                item
                xs={12}
                className="info-container"
                style={{ padding: "0px" }}
              >
                {loading ? (
                  <p className="no-result-text">Loading claims...</p>
                ) : claims.length === 0 ? (
                  <p className="no-result-text">
                    You have not made a claim yet. You can create your first
                    claim{" "}
                    <span
                      className="underline_text"
                      onClick={openShowClaimSelectionModal}
                    >
                      here!
                    </span>
                  </p>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead className="table-header-box">
                        <TableRow>
                          <TableCell align="left">Date Created</TableCell>
                          <TableCell align="left">Payer</TableCell>
                          <TableCell align="left">Claim Number</TableCell>
                          <TableCell align="left">Claim Type</TableCell>
                          <TableCell align="left">Total Amount</TableCell>
                          <TableCell align="center">Date Submitted</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center" width={"5%"}>
                            History
                          </TableCell>
                          <TableCell align="center" width={"5%"}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {claims.map((claim) => (
                          <TableRow
                            key={claim.id}
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                          >
                            <TableCell align="left">
                              {formatDate(claim.created_at)}
                            </TableCell>
                            <TableCell align="left">{claim.payor}</TableCell>
                            <TableCell align="left">
                              {claim.claim_number}
                            </TableCell>
                            <TableCell align="left">
                              {claim.claim_type || "N/A"}
                            </TableCell>
                            <TableCell align="left">
                              {claim.claim_status === "draft"
                                ? "N/A"
                                : formatAmount(claim.total_amount)}
                            </TableCell>
                            <TableCell align="center">
                              {claim.claim_status === "submitted" ||
                              claim.claim_status === "pending_submission" ? (
                                formatDate(claim.submitted_at)
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Button
                                    onClick={() =>
                                      history.push(
                                        `/patient_reports/${props.patientId}/claims_builder/${claim.id}?encounterBillingId=${props.encounterId}`
                                      )
                                    }
                                  >
                                    Edit
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {renderStatus(claim.claim_status)}
                            </TableCell>
                            <TableCell align="center">
                              <AccessTimeOutlinedIcon
                                style={{
                                  fontSize: 20,
                                  color: "black",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleShowClaimHistory(claim.id)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="center"
                              >
                                {claim.claim_status === "draft" ? (
                                  <DeleteIcon
                                    style={{
                                      fontSize: 20,
                                      color: "black",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => handleDeleteClaim(claim.id)}
                                  />
                                ) : (
                                  <VisibilityIcon
                                    style={{
                                      fontSize: 20,
                                      color: "black",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      history.push(
                                        `/patient_reports/${props.patientId}/claims_builder/${claim.id}`
                                      )
                                    }
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {showClaimHistory && (
        <ClaimHistory
          claimId={selectedClaimId}
          showClaimHistory={showClaimHistory}
          closeShowClaimHistoryModal={closeShowClaimHistoryModal}
        />
      )}

      {showClaimTypeSelectionModal && (
        <ClaimTypeSelection
          patient_id={props.patientId}
          encounterId={props.encounterId}
          modalOpen={showClaimTypeSelectionModal}
          setModalOpen={setShowClaimTypeSelectionModal}
          setShowCreateFirstTemplate={setShowCreateFirstTemplate}
          setShowChooseClaimTemplate={setShowChooseClaimTemplate}
          claimTemplates={claimTemplates}
          setClaimTemplates={setClaimTemplates}
        />
      )}

      {showCreateFirstTemplate && (
        <CreateFirstTemplateModal
          modalOpen={showCreateFirstTemplate}
          setModalOpen={setShowCreateFirstTemplate}
        />
      )}

      {showChooseClaimTemplate && (
        <ChooseClaimTemplateModal
          claimTemplates={claimTemplates}
          modalOpen={showChooseClaimTemplate}
          setModalOpen={setShowChooseClaimTemplate}
          patient_id={props.patientId}
          encounterId={props.encounterId}
        />
      )}
    </Grid>
  );
};

export default EncounterClaimInfoPanel;
